import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  NgForm,
  ValidatorFn,
} from '@angular/forms';
import { Observable } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { FormsModels } from '../../models/forms.model';

export type WithControls = NgForm | FormGroup | FormArray;

export function getControlCompletionPercentage(
  control: WithControls,
  includedControls: string[] = [],
  excludedControls: string[] = [],
) {
  if (!control) {
    return null;
  }

  if (includedControls && excludedControls) {
    throw new Error('includedControls and excludedControls are both set');
  }

  const controls = getAllControlsFiltered(
    control,
    includedControls,
    excludedControls,
  );
  const validLength = controls.filter((c) => c.valid).length;
  const perc = Math.round((validLength * 100) / controls.length);
  return Math.max(0, Math.min(perc, 100));
}

export function getAllControls(
  withControls: WithControls,
  recursive?: boolean,
): AbstractControl[] {
  const { controls }: any = withControls;
  let arr = Object.keys(controls).map((ctrlKey) => {
    return controls[ctrlKey];
  });

  if (recursive) {
    arr.forEach((innerCtrl) => {
      if (innerCtrl instanceof FormGroup) {
        arr.push(...getAllControls(innerCtrl, recursive));
      } else if (innerCtrl instanceof FormArray) {
        arr.push(...getAllControls(innerCtrl, recursive));
      }
    });
  }

  return arr;
}

export function getAllControlsFiltered(
  control: WithControls,
  includedControls: string[] | null = [],
  excludedControls: string[] = [],
): AbstractControl[] {
  if (!control) {
    return [];
  }

  const { controls }: any = control;
  let controlKeys = [];

  if (includedControls && includedControls.length > 0) {
    controlKeys = Object.keys(controls).filter(
      (ctrlKey) =>
        includedControls.indexOf(ctrlKey) !== -1 &&
        controls.hasOwnProperty(ctrlKey),
    );
  } else if (excludedControls && excludedControls.length > 0) {
    controlKeys = Object.keys(controls).filter(
      (ctrlKey) => excludedControls.indexOf(ctrlKey) === -1,
    );
  } else {
    controlKeys = Object.keys(controls);
  }

  return controlKeys.map((ck) => controls[ck]);
}

export function markAllControlsAsDirty(
  withControls: WithControls,
  recursive?: boolean,
) {
  getAllControls(withControls, recursive).forEach((ctrl) => ctrl.markAsDirty());
}

export function markAllControlsAsDirtyAndUpdateValidity(
  withControls: WithControls,
  recursive?: boolean,
) {
  getAllControls(withControls, recursive).forEach(
    markControlAsDirtyAndUpdateValidity,
  );
}

function markControlAsDirtyAndUpdateValidity(ctrl: AbstractControl) {
  ctrl.markAsDirty();
  ctrl.updateValueAndValidity();
}

export function markAllControlsAsPristine(withControls: WithControls) {
  getAllControls(withControls).forEach((control) => {
    control.markAsPristine();
  });
}

export function arrayToSelectListItemArray<T>(
  arr: T[],
  converter: (item: T) => FormsModels.SelectListItem<any>,
): FormsModels.SelectListItem<T>[] {
  return arr.map(converter);
}

export const range = (start: number, end: number, step = 1): number[] =>
  Array(Math.floor((end - start) / step) + 1)
    .fill(0)
    .map((_, idx) => start + idx * step);

export function stringArrayToSelectListItemArray(
  arr: string[],
  empty?: FormsModels.SelectListItem,
): FormsModels.SelectListItem[] {
  return arr.map((v) => {
    if (v || !empty) {
      return { text: v, value: v };
    } else {
      return empty;
    }
  });
}

export function getFormGroupWithValueChanges(
  fb: FormBuilder,
  formState: any,
  validators: ValidatorFn | ValidatorFn[],
  onValueChanges: (fc: FormGroup) => void,
  takeUntilObs: Observable<void>,
  debounce?: number,
): FormGroup {
  const grp = fb.group(formState, validators);
  const obs = debounce
    ? grp.valueChanges.pipe(debounceTime(debounce))
    : grp.valueChanges;
  obs.pipe(takeUntil(takeUntilObs)).subscribe((_) => {
    onValueChanges(grp);
  });
  return grp;
}

export function getControlWithValueChanges(
  fb: FormBuilder,
  formState: any,
  validators: ValidatorFn | ValidatorFn[],
  onValueChanges: (fc: FormControl) => void,
  takeUntilObs: Observable<void>,
  debounce?: number,
): FormControl {
  const ctrl = fb.control(formState, validators);
  const obs = debounce
    ? ctrl.valueChanges.pipe(debounceTime(debounce))
    : ctrl.valueChanges;
  obs.pipe(takeUntil(takeUntilObs)).subscribe((_) => {
    onValueChanges(ctrl);
  });
  return ctrl;
}

export function hasValidator(
  control: AbstractControl,
  errKey: string,
): boolean {
  if (!control || !control.validator) {
    return false;
  }
  const result = control.validator(new FormControl());
  return !!result && result.hasOwnProperty(errKey);
}

export function applyFormConfig(withControls: WithControls, formConfig: any) {
  if (!formConfig) {
    Object.keys(withControls.controls).forEach((key) => {
      (withControls.controls as { [key: string]: AbstractControl })[
        key
      ].disable();
    });
  } else {
    Object.keys(formConfig).forEach((key) => {
      const isEnabled = formConfig[key];
      const ctrl: AbstractControl = (
        withControls.controls as { [key: string]: AbstractControl }
      )[key];
      if (ctrl) {
        if (isEnabled) {
          ctrl.enable();
        } else {
          ctrl.disable();
        }
      } else {
        console.warn('control not found with key : ' + key);
      }
    });
  }
}

export function isWithControls(
  person: NgForm | FormGroup | FormArray | AbstractControl,
): person is WithControls {
  return (<WithControls>person).controls !== undefined;
}
